import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SpotifyGetReportDocument, SpotifyReportsQueryItems } from 'wb-spotify/graphql/generated';
import useSetCreateRedditReportData from 'wb-reddit/app/routes/reports/hooks/useSetCreateRedditReportData';
import { RedditReport } from 'wb-reddit/app/routes/reports/components/report';
import useCreateRedditReport from 'wb-reddit/app/routes/reports/create/useCreateRedditReport';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { handleErrorNotification } from '@aleph/shared-components';
import { processSpotifyReport } from 'wb-spotify/app/routes/reports/utils';

/**
 * For now only implement for spotify since reddit cant be tested
 * What this hook does is it fetches the report data when the report id is present in the url.
 * Then it sets the report data in the create report store which causes the single report component to rendered.
 */
const useRedditReportUrlOverlay = () => {
	const { params: editReportId } = useParams();
	const setEditingReportData = useSetCreateRedditReportData();
	const { cancelCreation } = useCreateRedditReport();
	const [fetchReportData] = useLazyQuery(SpotifyGetReportDocument, {
		// Disable cache so we get the latest report data
		fetchPolicy: 'network-only',
	});

	const { t } = useTranslation();
	const navigate = useNavigate();

	const setEditingReport = async (reportId?: string) => {
		if (!reportId) {
			cancelCreation();

			return;
		}

		const { data, error } = await fetchReportData({ variables: { reportId } });
		const report = data?.reports?.items?.[0] as unknown as RedditReport;

		if (error || !report) {
			captureException(error);

			handleErrorNotification(
				error,
				t('re: something went wrong while fetching report', 'Something went wrong while fetching report'),
			);

			navigate('/reports');

			return;
		}

		setEditingReportData(processSpotifyReport(report as unknown as SpotifyReportsQueryItems));
	};

	useEffect(() => {
		setEditingReport(editReportId);
	}, [editReportId]);
};

export default useRedditReportUrlOverlay;
